import gsap from 'gsap';

/**
 * Animation d'entrée du hub : le titre s'allume comme une enseigne néon,
 * le texte apparaît, ses mots-clés se soulignent un instant (puis le soulignement
 * s'efface), puis la boîte de statut et les boutons arrivent en cascade.
 * À placer sur le .wrapper du hub.
 */
export default class HubIntro {
  constructor(element) {
    this.element = element;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const q = gsap.utils.selector(this.element);
    const top = q('.logo-name .top');
    const bottom = q('.logo-name .bottom');
    const text = q('.intro-text p');
    const marks = q('.intro-text strong, .status-box strong');
    const status = q('.status-box');
    const loupe = q('.status-box svg');
    const buttons = q('.button-bar .button');

    // Soulignement temporaire des <strong> (voir .hubmark dans hubfx.scss)
    marks.forEach((m) => m.classList.add('hubmark'));

    this.ctx = gsap.context(() => {
      // Le titre est éteint avant l'enseigne
      gsap.set([...top, ...bottom], { opacity: 0 });

      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      // Allumage d'une enseigne néon : quelques sursauts, puis lumière stable.
      // clearProps rend ensuite l'opacité au NeonGlitch.
      const ignite = (target, at) =>
        tl.to(
          target,
          {
            keyframes: [
              { opacity: 0.9, duration: 0.05 },
              { opacity: 0.1, duration: 0.08 },
              { opacity: 1, duration: 0.05 },
              { opacity: 0.25, duration: 0.09 },
              { opacity: 1, duration: 0.5, ease: 'power2.out' },
            ],
            ease: 'none',
            onComplete: () => gsap.set(target, { clearProps: 'opacity' }),
          },
          at,
        );

      ignite(top, 0.15);
      ignite(bottom, 0.55);

      tl.from(
        text,
        {
          y: 24,
          opacity: 0,
          filter: 'blur(8px)',
          duration: 0.9,
          clearProps: 'transform,opacity,filter',
        },
        1.0,
      );

      // Chaque mot-clé se souligne (le trait se dessine de gauche à droite), puis le trait
      // s'efface vers la droite : le texte retrouve son apparence normale
      marks.forEach((mark, i) => {
        const at = 1.4 + i * 0.14;
        tl.to(mark, { '--b': 1, duration: 0.5, ease: 'power2.out' }, at);
        tl.to(mark, { '--a': 1, duration: 0.5, ease: 'power2.in' }, at + 0.8);
      });

      tl.from(
        status,
        { y: 30, opacity: 0, duration: 0.8, clearProps: 'transform,opacity' },
        1.6,
      );
      tl.from(
        loupe,
        {
          scale: 0.4,
          rotation: -30,
          duration: 0.8,
          ease: 'back.out(2)',
          clearProps: 'transform',
        },
        1.7,
      );

      tl.from(
        buttons,
        {
          y: 40,
          scale: 0.9,
          opacity: 0,
          duration: 0.7,
          ease: 'back.out(1.5)',
          stagger: 0.07,
          clearProps: 'transform,opacity', // rend la main au CSS et au MagneticButtons
        },
        1.9,
      );

      // Fin de l'animation : on retire le soulignement du DOM
      tl.call(() => {
        marks.forEach((m) => {
          m.classList.remove('hubmark');
          m.style.removeProperty('--a');
          m.style.removeProperty('--b');
        });
      });
    }, this.element);
  }

  destroy() {
    this.ctx?.revert();
  }
}
